import React, { useCallback, useMemo } from 'react'
import { useLiveSpaceSummary } from '@components/Live/LiveClientProvider'
import { SpaceIcon } from '@components/SpaceIcon'
import { Badge, Box, Dot, Icon, MotionBox } from '@ui'
import { AnimatePresence } from 'framer-motion'
import { Analytics } from 'hooks/useAnalytics'
import { useDevice } from 'hooks/useDevice'
import { useShowHasUnreadBadgeForSpaceId } from 'hooks/useSpaceUnreadsIgnoreMuted'
import { useUserStore } from 'store/userSettingsStore'
import { IconName } from 'ui/components/Icon'
import { useSizeContext } from 'ui/hooks/useSizeContext'
import { ToneName } from 'ui/styles/themes'
import { vars } from 'ui/styles/vars.css'
import { useMyUserId, useSpaceNotificationCounts } from 'use-towns-client'

import { MainSideBarNavItem } from './_NavItem'
import * as styles from './SpaceNavItem.css'

type Props = {
    id: string
    spaceName: string
    selected?: boolean
    icon?: IconName
    onClick?: () => void
}

export const SpaceNavItem = React.memo(function SpaceNavItem(props: Props) {
    const { id, spaceName, icon } = props

    const { isTouch } = useDevice()
    const userId = useMyUserId()
    const notificationCounts = useSpaceNotificationCounts(id)
    const mentions = notificationCounts.mentions
    const newMessages = useShowHasUnreadBadgeForSpaceId(id)
    const sizeContext = useSizeContext()
    const isSmall = sizeContext.lessThan(180)

    // Live state
    const spaceSummary = useLiveSpaceSummary(id)
    const liveState = useMemo(() => {
        if (!spaceSummary) {
            return { hasSubscribers: false, hasListeners: false, hasStage: false }
        }

        const hasSubscribers = spaceSummary.state.subscribers.totalCount > 1

        // Check all channels for listeners and stage users
        let totalListeners = 0
        let totalStage = 0

        if (spaceSummary.channels) {
            for (const channelState of Object.values(spaceSummary.channels)) {
                totalListeners += channelState.listeners.totalCount
                totalStage += channelState.stage.totalCount
            }
        }

        return {
            hasSubscribers,
            hasListeners: totalListeners > 0,
            hasStage: totalStage > 0,
        }
    }, [spaceSummary])

    // Determine which live class to apply
    const liveClassName = useMemo(() => {
        if (props.selected && (liveState.hasStage || liveState.hasListeners)) {
            return styles.liveGlowClass
        }
        return undefined
    }, [liveState, props.selected])

    const [setFavoriteSpaces, favoriteSpaces] = useUserStore((s) => [
        s.setFavoriteSpaces,
        (userId && s.users[userId]?.favoriteSpaces) || [],
    ])

    const isFavorite = favoriteSpaces.includes(id)

    const onToggleFavorite = useCallback(() => {
        if (!userId) {
            return
        }
        setFavoriteSpaces(
            userId,
            favoriteSpaces.includes(id)
                ? favoriteSpaces.filter((f) => f !== id)
                : [id, ...favoriteSpaces],
        )
        const eventName = isFavorite
            ? 'clicked remove space from favorites'
            : 'clicked add space to favorites'
        Analytics.getInstance().track(eventName)
    }, [favoriteSpaces, id, isFavorite, setFavoriteSpaces, userId])

    const [isHovered, setIsHovered] = React.useState(false)
    const onMouseEnter = useCallback(() => setIsHovered(true), [])
    const onMouseLeave = useCallback(() => setIsHovered(false), [])

    return (
        <MainSideBarNavItem
            isSpace
            id={id}
            aria-label={spaceName}
            tooltip={isTouch ? undefined : spaceName}
            selected={props.selected}
            onClick={props.onClick}
        >
            <Box
                position="relative"
                className={liveClassName}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <SpaceIcon
                    fadeIn
                    width="x4"
                    minWidth="x4"
                    aspectRatio="1/1"
                    spaceId={id}
                    background="level2"
                    rounded="sm"
                    variant="thumbnail100"
                    firstLetterOfSpaceName={spaceName?.trim()[0] ?? ''}
                />
                {isTouch && newMessages ? (
                    <Dot position="topRight" style={{ transform: `translate(25%,-25%)` }} />
                ) : (
                    <></>
                )}

                {!isTouch && (
                    <IconNotification
                        spaceId={id}
                        hasUnread={newMessages}
                        mentionCount={mentions}
                        isHovered={isHovered}
                        isFavorite={isFavorite}
                        onToggleFavorite={onToggleFavorite}
                    />
                )}

                {liveState.hasStage && (
                    <Box className={styles.microphoneOverlayClass}>
                        <Icon
                            type="microphone"
                            background="positive"
                            color="inverted"
                            rounded="full"
                            style={{
                                width: '15px',
                                height: '15px',
                                padding: '1px',
                                border: `2px solid ${vars.color.background.level1}`,
                                borderRadius: 'full',
                            }}
                        />
                    </Box>
                )}
            </Box>

            {icon && <Icon type={icon} color="gray2" background="level2" size="square_lg" />}

            <Box shrink display={isSmall ? 'none' : undefined} color="gray2">
                <Badge value={mentions} />
            </Box>
        </MainSideBarNavItem>
    )
})

SpaceNavItem.displayName = 'SpaceNavItem'

const IconNotification = (props: {
    spaceId: string
    hasUnread: boolean
    mentionCount: number
    isFavorite: boolean
    isHovered: boolean
    onToggleFavorite: () => void
}) => {
    const { hasUnread, mentionCount, isFavorite, isHovered, onToggleFavorite } = props
    const hasMention = mentionCount > 0
    const mentionCountString = useMemo(
        () => (mentionCount < 10 ? mentionCount : '9+'),
        [mentionCount],
    )
    const onClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            onToggleFavorite()
        },
        [onToggleFavorite],
    )

    return (
        <AnimatePresence mode="wait">
            {isHovered || isFavorite ? (
                <MotionBox
                    key="star"
                    position="topRight"
                    initial={{
                        scale: 0.5,
                        x: '40%',
                        y: '-40%',
                    }}
                    animate={{
                        scale: 1,
                        color: hasUnread
                            ? vars.color.foreground[ToneName.Accent]
                            : isFavorite
                              ? vars.color.foreground.gray2
                              : vars.color.foreground.gray2,
                        '--background': vars.color.background.level1,
                    }}
                    whileHover={{
                        scale: isFavorite ? 0.9 : 1.2,
                    }}
                    onClick={onClick}
                    {...motionPreset}
                >
                    {hasMention ? (
                        <Badge value={mentionCountString}>{mentionCountString}</Badge>
                    ) : (
                        <Icon type="starFilledOutline" size="square_xs" />
                    )}
                </MotionBox>
            ) : hasUnread ? (
                <MotionBox
                    position="topRight"
                    key="dot"
                    initial={{ x: '30%', y: '-30%', scale: 0.5 }}
                    animate={{ scale: 1 }}
                    {...motionPreset}
                >
                    {hasMention ? (
                        <Badge value={mentionCountString}>{mentionCountString}</Badge>
                    ) : (
                        <Dot />
                    )}
                </MotionBox>
            ) : (
                <></>
            )}
        </AnimatePresence>
    )
}

const motionPreset = { transition: { type: 'spring' as const, stiffness: 200, damping: 20 } }
